import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Chip, IconButton, Avatar, Tooltip, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import client from '../api/client';
import LogoLoader from '../components/Common/LogoLoader';

const API = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace('/api/auth', '/api/ai-agents')
  : 'http://localhost:5000/api/ai-agents';

const timeAgo = (d) => {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(d).toLocaleDateString();
};

export default function AIAgentChats() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dark, setDark] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [resolveOpen, setResolveOpen] = useState(false);
  const chatRef = useRef(null);
  const pollRef = useRef(null);

  const load = (silent) => {
    if (!silent) setLoading(true);
    client.get(`${API}/${id}/conversations`)
      .then(r => { setConvos(r.data); if (selected) { const u = r.data.find(c => c._id === selected._id); if (u) setSelected(u); } })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); pollRef.current = setInterval(() => load(true), 5000); return () => clearInterval(pollRef.current); }, [id]);
  useEffect(() => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; }, [selected]);

  const handleReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await client.post(`${API}/${id}/conversations/${selected._id}/reply`, { text: reply.trim() });
      setReply(''); load(true);
    } finally { setSending(false); }
  };

  const handleResolve = async () => {
    if (!selected) return;
    await client.put(`${API}/${id}`, {}); // Simple - mark via conversation update
    await client.post(`${API}/${id}/conversations/${selected._id}/reply`, { text: '✅ This conversation has been resolved. Thank you!' });
    setResolveOpen(false); load(true);
  };

  // Stats
  const stats = {
    total: convos.length,
    active: convos.filter(c => c.status === 'active').length,
    human: convos.filter(c => c.status === 'human_requested').length,
    resolved: convos.filter(c => c.status === 'resolved').length,
  };

  // Filter & Search
  const filtered = convos
    .filter(c => filter === 'all' || c.status === filter)
    .filter(c => !search || (c.visitorName || c.visitorEmail || '').toLowerCase().includes(search.toLowerCase()));

  // Theme
  const T = dark
    ? { bg: '#0f172a', panel: '#1e293b', card: '#1e293b', border: '#334155', text: '#f1f5f9', sub: '#94a3b8', input: '#0f172a', accent: '#6366f1' }
    : { bg: '#f8fafc', panel: '#fff', card: '#fff', border: '#e2e8f0', text: '#111827', sub: '#6b7280', input: '#f9fafb', accent: '#6366f1' };

  if (loading && convos.length === 0) return <LogoLoader message="Loading conversations..." />;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 70px)', bgcolor: T.bg, overflow: 'hidden' }}>
      {/* Left - List */}
      <Box sx={{ width: 320, flexShrink: 0, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', bgcolor: T.panel }}>
        {/* Header */}
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${T.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={() => navigate('/app/ai-agents')} sx={{ color: T.sub }}><ArrowBackIcon fontSize="small" /></IconButton>
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: T.text, flex: 1 }}>Chats</Typography>
            <Tooltip title="Refresh"><IconButton size="small" onClick={() => load()} sx={{ '&:hover svg': { transform: 'rotate(-360deg)' }, '& svg': { transition: 'transform 0.5s' } }}><RefreshIcon fontSize="small" sx={{ color: T.accent }} /></IconButton></Tooltip>
            <IconButton size="small" onClick={() => setDark(d => !d)}>{dark ? <LightModeIcon fontSize="small" sx={{ color: '#f59e0b' }} /> : <DarkModeIcon fontSize="small" sx={{ color: '#6366f1' }} />}</IconButton>
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            {[{ l: 'All', v: stats.total, c: T.accent }, { l: '🙋', v: stats.human, c: '#f59e0b' }, { l: '✅', v: stats.resolved, c: '#10b981' }].map(s => (
              <Box key={s.l} sx={{ flex: 1, textAlign: 'center', py: 0.5, borderRadius: 1.5, bgcolor: `${s.c}15`, border: `1px solid ${s.c}30` }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: s.c }}>{s.v}</Typography>
                <Typography sx={{ fontSize: 9, color: T.sub }}>{s.l}</Typography>
              </Box>
            ))}
          </Box>

          {/* Search */}
          <TextField size="small" fullWidth placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.sub }} /></InputAdornment> }}
            sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: T.input, color: T.text, height: 32, '& fieldset': { borderColor: T.border } }, '& input': { color: T.text, fontSize: 12 } }} />

          {/* Filter */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {['all', 'active', 'human_requested', 'resolved'].map(f => (
              <Chip key={f} label={f === 'human_requested' ? '🙋 Human' : f === 'all' ? 'All' : f} size="small"
                onClick={() => setFilter(f)}
                sx={{ fontSize: 9, height: 22, fontWeight: filter === f ? 700 : 500, bgcolor: filter === f ? `${T.accent}20` : 'transparent', color: filter === f ? T.accent : T.sub, border: `1px solid ${filter === f ? T.accent : T.border}` }} />
            ))}
          </Box>
        </Box>

        {/* List */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {filtered.length === 0 && <Typography sx={{ p: 3, textAlign: 'center', color: T.sub, fontSize: 12 }}>No conversations</Typography>}
          {filtered.map(c => (
            <Box key={c._id} onClick={() => setSelected(c)}
              sx={{ p: 1.5, cursor: 'pointer', borderBottom: `1px solid ${T.border}`,
                bgcolor: selected?._id === c._id ? `${T.accent}15` : 'transparent',
                borderLeft: c.status === 'human_requested' ? '3px solid #f59e0b' : '3px solid transparent',
                '&:hover': { bgcolor: `${T.accent}08` } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: `${T.accent}20`, color: T.accent, fontSize: 13, fontWeight: 700 }}>
                  {(c.visitorName || 'V')[0].toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.text }} noWrap>{c.visitorName || 'Visitor'}</Typography>
                    <Typography sx={{ fontSize: 9, color: T.sub }}>{timeAgo(c.updatedAt)}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 10, color: T.sub }}>{c.messages?.length || 0} msgs</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Center - Chat */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: T.bg }}>
        {!selected ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <SmartToyIcon sx={{ fontSize: 48, color: T.sub, opacity: 0.5 }} />
            <Typography sx={{ color: T.sub, fontSize: 13 }}>Select a conversation</Typography>
          </Box>
        ) : (
          <>
            {/* Chat Header */}
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: T.panel }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: `${T.accent}20`, color: T.accent, fontSize: 12, fontWeight: 700 }}>
                {(selected.visitorName || 'V')[0].toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.text }}>{selected.visitorName || 'Visitor'}</Typography>
                <Typography sx={{ fontSize: 10, color: T.sub }}>{selected.messages?.length} messages</Typography>
              </Box>
              <Button size="small" startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />} onClick={() => setResolveOpen(true)}
                sx={{ textTransform: 'none', fontSize: 11, fontWeight: 700, borderRadius: 2, bgcolor: '#05966920', color: '#059669' }}>
                Resolve
              </Button>
            </Box>

            {/* Messages */}
            <Box ref={chatRef} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {(selected.messages || []).map((m, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: m.sender === 'visitor' ? 'flex-end' : 'flex-start', mb: 1.2, gap: 0.8 }}>
                  {m.sender !== 'visitor' && (
                    <Avatar sx={{ width: 24, height: 24, fontSize: 9, fontWeight: 700,
                      bgcolor: m.sender === 'owner' ? '#05966920' : `${T.accent}20`,
                      color: m.sender === 'owner' ? '#059669' : T.accent }}>
                      {m.sender === 'owner' ? '👤' : '🤖'}
                    </Avatar>
                  )}
                  <Box sx={{ maxWidth: '68%' }}>
                    <Box sx={{ px: 1.5, py: 1,
                      borderRadius: m.sender === 'visitor' ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
                      bgcolor: m.sender === 'visitor' ? T.accent : m.sender === 'owner' ? (dark ? '#064e3b' : '#ecfdf5') : (dark ? '#1e293b' : '#f3f4f6'),
                      color: m.sender === 'visitor' ? '#fff' : T.text,
                      border: m.sender === 'visitor' ? 'none' : `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: 12.5, lineHeight: 1.5 }}>{m.text}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 9, color: T.sub, mt: 0.2, textAlign: m.sender === 'visitor' ? 'right' : 'left' }}>
                      {timeAgo(m.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Reply */}
            <Box sx={{ p: 1.5, borderTop: `1px solid ${T.border}`, bgcolor: T.panel }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField fullWidth size="small" value={reply} onChange={e => setReply(e.target.value)}
                  placeholder="Reply to visitor..." onKeyDown={e => e.key === 'Enter' && handleReply()}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: T.input, color: T.text, '& fieldset': { borderColor: T.border } }, '& input': { color: T.text, fontSize: 13 } }} />
                <Button variant="contained" onClick={handleReply} disabled={!reply.trim() || sending}
                  sx={{ borderRadius: 2, minWidth: 40, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
                  <SendIcon sx={{ fontSize: 16 }} />
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Right - Visitor Info */}
      {selected && (
        <Box sx={{ width: 240, flexShrink: 0, borderLeft: `1px solid ${T.border}`, bgcolor: T.panel, p: 2, display: { xs: 'none', md: 'block' } }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.text, mb: 2 }}>Visitor Info</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 16, color: T.sub }} />
              <Typography sx={{ fontSize: 12, color: T.text }}>{selected.visitorName || 'Unknown'}</Typography>
            </Box>
            {selected.visitorEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ fontSize: 16, color: T.sub }} />
                <Typography sx={{ fontSize: 12, color: T.text, flex: 1 }} noWrap>{selected.visitorEmail}</Typography>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(selected.visitorEmail)}><ContentCopyIcon sx={{ fontSize: 12, color: T.sub }} /></IconButton>
              </Box>
            )}
            {selected.visitorPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 16, color: T.sub }} />
                <Typography sx={{ fontSize: 12, color: T.text }}>{selected.visitorPhone}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatIcon sx={{ fontSize: 16, color: T.sub }} />
              <Typography sx={{ fontSize: 12, color: T.text }}>{selected.messages?.length || 0} messages</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: T.sub }} />
              <Typography sx={{ fontSize: 12, color: T.text }}>{timeAgo(selected.createdAt)}</Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 3, p: 1.5, bgcolor: `${T.accent}10`, borderRadius: 2, border: `1px solid ${T.accent}20` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.accent, mb: 0.5 }}>Status</Typography>
            <Chip label={selected.status === 'human_requested' ? '🙋 Human Requested' : selected.status} size="small"
              sx={{ fontWeight: 700, fontSize: 10,
                bgcolor: selected.status === 'human_requested' ? '#fef3c7' : selected.status === 'resolved' ? '#ecfdf5' : `${T.accent}20`,
                color: selected.status === 'human_requested' ? '#b45309' : selected.status === 'resolved' ? '#059669' : T.accent }} />
          </Box>
        </Box>
      )}

      {/* Resolve Dialog */}
      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15 }}>Resolve Conversation?</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: 13, color: '#6b7280' }}>This will mark the conversation as resolved and send a closing message to the visitor.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResolveOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleResolve} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#059669', borderRadius: 2 }}>Resolve</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
